import type {
  ElementFileUploadEvent,
  QuestionFileModelData,
  SurveyImageAnswerValue,
} from '@coorum-research/shared-common/types'
import type { FileDropItem } from 'react-aria-components'
import type { SurveyElementFormHandler } from './survey-element-form'
import { acceptedTypesForImages } from '@coorum-research/shared-common/shared/survey/survey-validation'
import { MOBILE_DEVICE_REGEX } from '@coorum-research/shared-common/utils/device.util'
import { clsx } from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, DropZone, FileTrigger } from 'react-aria-components'
import { coorumColors } from '../../survey/coorumTheme'
import { CameraIcon } from '../icons/CameraIcon'
import { LibraryIcon } from '../icons/LibraryIcon'
import { PlusIcon } from '../icons/PlusIcon'
import { TrashIcon } from '../icons/TrashIcon'
import { Spinner } from '../Spinner'
import { useTheme } from '../ThemeContext'
import { handleFileUpload, validateFile } from './survey-element-form-handler-file'

type Props = {
  elementJson: QuestionFileModelData
  defaultValue: SurveyImageAnswerValue[]
  onUploadFiles?: (option: ElementFileUploadEvent) => Promise<void> | void
  onUploadingChange?: (isUploading: boolean) => void
  onValidationError: (errorMessage: string) => void
  onValueChange: (value: SurveyImageAnswerValue[]) => void
  currentForm: SurveyElementFormHandler
}

export function ElementFileForm({
  elementJson,
  defaultValue,
  onUploadFiles,
  onUploadingChange,
  onValidationError,
  onValueChange,
  currentForm,
}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const themeJson = useTheme()

  // カメラ起動専用ボタンのinputタグにcapture属性を追加
  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'camera')
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setFile(null)
    if (defaultValue.length > 0) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setImageUrl(defaultValue[0].content || '')
    }
  }, [defaultValue])

  const handleImageDelete = useCallback(() => {
    setFile(null)
    setImageUrl('')
    onValueChange([]) // 空の配列を渡す
  }, [onValueChange])

  const handleFileChange = useCallback(async (file: File) => {
    setFile(file)
    onValidationError?.('')

    // ファイルのバリデーション
    const validationResult = validateFile(file, elementJson.isRequired)
    if (!validationResult.isValid) {
      onValidationError?.(validationResult.errorMessage || '')
      setFile(null)
      return
    }

    if (!onUploadFiles) {
      return
    }

    setImageUrl('') // アップロード開始時にリセット
    const uploadedFiles = [file]

    try {
      const options = {
        files: uploadedFiles,
        callback: (_callbackResults: any[], errors: string[]) => {
          if (errors && errors.length > 0) {
            console.error('Upload error:', errors)
            onValidationError?.('アップロード中にエラーが発生しました。')
            return
          }
        },
      } as ElementFileUploadEvent

      const updatedEvent = await handleFileUpload(elementJson.name, options, {
        onUploadingChange: (isUploading: boolean) => {
          if (currentForm?.setUploadingState) {
            currentForm.setUploadingState(isUploading)
          }
          onUploadingChange?.(isUploading)
        },
        onValidationError,
        onValueChange: (_name: string, value: SurveyImageAnswerValue[]) => {
          if (value.length > 0) {
            setImageUrl(value[0].content)
          }
          onValueChange(value)
        },
      })

      if (updatedEvent) {
        await onUploadFiles(updatedEvent)
      }
    } catch (error) {
      console.error('Upload failed:', error)
      onValidationError?.('アップロードに失敗しました。')
    }
  }, [onValueChange, onUploadFiles, elementJson, onUploadingChange, onValidationError, currentForm])

  // PCかどうかの判定
  const isPC = !MOBILE_DEVICE_REGEX.test(navigator.userAgent)

  return (
    <div
      className={
        clsx(
          !imageUrl ? '' : ' bg-[#F6F6F6]',
          'relative w-full',
        )
      }
    >
      <DropZone
        onDrop={async (e: {
          items: any[]
        }) => {
          const files = e.items.filter((file: {
            kind: string
          }) => file.kind === 'file') as FileDropItem[]
          const file = await files[0].getFile()
          await handleFileChange(file)
        }}
      >
        {/* アップロード後のサムネイル表示 */}
        {imageUrl && (
          <img
            src={imageUrl}
            className="absolute inset-y-0 m-auto max-h-[300px] w-full rounded object-contain"
            alt="ボタン画像"
          />
        )}

        <div
          className={
            clsx(
              isPC ? 'border border-dashed border-[#1B1C1B1F]' : '',
              'flex h-[300px] min-h-[160px] flex-col items-center justify-center rounded py-[32px]',
            )
          }
          style={{
            color: themeJson?.mainTextColor,
            backgroundColor: currentForm?.getUploadingState?.() ? coorumColors.secondaryColor : '',
          }}
        >
          {currentForm?.getUploadingState?.()
            ? (
              <Spinner fill={themeJson ? themeJson.mainColor : undefined} />
            )
            : (
              <>
                {!file && !imageUrl
                  ? (
                    <div className="w-full text-center">
                      {isPC
                        ? (
                          <>
                            <p>
                              画像をここにドロップ
                            </p>
                            <p className="my-6 text-[12px] font-bold">
                              または
                            </p>

                            <FileTrigger
                              acceptedFileTypes={acceptedTypesForImages}
                              allowsMultiple={false}
                              onSelect={async (e) => {
                                if (!e) {
                                  return
                                }
                                const file = Array.from(e)[0]
                                await handleFileChange(file)
                              }}
                            >
                              <Button
                                className={clsx('mt-2 rounded border border-solid border-[#1B1C1B] bg-white px-8 py-[6px]')}
                              >
                                <label
                                  className="flex items-center text-[14px] font-bold"
                                >
                                  <PlusIcon className="mr-2 size-4" fill={themeJson?.mainTextColor} />
                                  <p>
                                    画像を選択してアップロード
                                  </p>
                                </label>
                              </Button>
                            </FileTrigger>
                          </>
                        ) : (
                          <>
                            {/* スマホ表示 */}
                            <div className="flex h-full flex-col items-center justify-center py-[32px]">
                              {/* カメラ起動専用ボタン */}
                              <FileTrigger
                                acceptedFileTypes={acceptedTypesForImages}
                                allowsMultiple={false}
                                ref={fileInputRef} // inputタグにcapture属性を追加
                                onSelect={async (e) => {
                                  if (!e) {
                                    return
                                  }
                                  const file = Array.from(e)[0]
                                  await handleFileChange(file)
                                }}
                              >
                                <Button
                                  className="mt-2 w-full rounded border border-solid border-[#1B1C1B1F] bg-white px-8 py-[6px]"
                                >
                                  <label
                                    className="flex items-center justify-center text-[14px] font-bold"
                                  >
                                    <CameraIcon className="mr-2 size-4" fill={themeJson?.mainTextColor} />
                                    <p>
                                      カメラを起動
                                    </p>
                                  </label>
                                </Button>
                              </FileTrigger>

                              <FileTrigger
                                acceptedFileTypes={acceptedTypesForImages}
                                allowsMultiple={false}
                                onSelect={async (e) => {
                                  if (!e) {
                                    return
                                  }
                                  const file = Array.from(e)[0]
                                  await handleFileChange(file)
                                }}
                              >
                                <Button
                                  className={clsx('mt-2 w-full rounded border border-solid border-[#1B1C1B1F] bg-white px-8 py-[6px]')}
                                >
                                  <label
                                    className="flex items-center justify-center text-[14px] font-bold"
                                  >
                                    <LibraryIcon className="mr-2 size-4" fill={themeJson?.mainTextColor} />
                                    <p>
                                      ライブラリから画像を選択
                                    </p>
                                  </label>
                                </Button>
                              </FileTrigger>
                            </div>
                          </>
                        )}
                    </div>
                  ) : (
                    // 画像がアップロードされて後に表示する、右上の丸アイコン
                    <>
                      {/* 削除ボタン */}
                      <div className="absolute right-2 top-2 flex size-[32px] items-center justify-center rounded-full border border-[#F6F6F6] bg-white">
                        {/* ※ buttonタグだと警告＋ButtonコンポーネントだとFileTriggerに誤認識されるので、divで代用 */}
                        <div
                          onClick={handleImageDelete}
                        >
                          <TrashIcon className="size-4" fill={themeJson?.mainTextColor} />
                        </div>
                      </div>

                      {/* ファイル再選択ボタン */}
                      <div className="absolute right-12 top-2 flex size-[32px] items-center justify-center rounded-full border border-[#F6F6F6] bg-white">
                        <FileTrigger
                          acceptedFileTypes={acceptedTypesForImages}
                          allowsMultiple={false}
                          onSelect={async (e) => {
                            if (!e) {
                              return
                            }
                            const file = Array.from(e)[0]
                            await handleFileChange(file)
                          }}
                        >
                          <Button>
                            <LibraryIcon className="size-4" fill={themeJson?.mainTextColor} />
                          </Button>
                        </FileTrigger>
                      </div>

                      {/* カメラ起動ボタン（スマホのみ） */}
                      {!isPC && (
                        <div className="absolute right-[5.5rem] top-2 flex size-[32px] items-center justify-center rounded-full border border-[#F6F6F6] bg-white">
                          <FileTrigger
                            acceptedFileTypes={acceptedTypesForImages}
                            ref={fileInputRef} // inputタグにcapture属性を追加
                            allowsMultiple={false}
                            onSelect={async (e) => {
                              if (!e) {
                                return
                              }
                              const file = Array.from(e)[0]
                              await handleFileChange(file)
                            }}
                          >
                            <Button>
                              <CameraIcon className="size-4" fill={themeJson?.mainTextColor} />
                            </Button>
                          </FileTrigger>
                        </div>
                      )}
                    </>
                  )}
              </>
            )}
        </div>
      </DropZone>
    </div>
  )
}
